// src/contexts/AuthContext.js
import React, {
	createContext,
	useContext,
	useEffect,
	useState,
	useMemo,
} from "react";
import { supabase } from "../config/supabase";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let refreshAttempts = 0;
		const MAX_REFRESH_ATTEMPTS = 3;
		let isHandlingAuth = false;
		let lastRefreshTime = 0;
		const REFRESH_COOLDOWN = 10000; // 10 seconds cooldown between refresh attempts

		const handleAuthChange = async (session) => {
			// Prevent concurrent auth change handlers
			if (isHandlingAuth) {
				return;
			}

			isHandlingAuth = true;
			try {
				if (session?.user) {
					// Fetch user profile with error handling and retry logic
					let profile = null;
					let profileError = null;
					let retryCount = 0;
					const MAX_RETRIES = 2;
					let currentSession = session;

					while (retryCount <= MAX_RETRIES) {
						const result = await supabase
							.from("profiles")
							.select("*")
							.eq("id", currentSession.user.id)
							.single();

						profile = result.data;
						profileError = result.error;

						// If successful, break out of retry loop
						if (!profileError) {
							break;
						}

						// If it's a 406 error (token issue), wait and retry
						if ((profileError.status === 406 || profileError.code === "PGRST301") && retryCount < MAX_RETRIES) {
							console.warn(`Profile fetch returned 406 (attempt ${retryCount + 1}/${MAX_RETRIES + 1}) - waiting for token refresh...`);
							// Wait a bit for token to refresh
							await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
							
							// Try to get a fresh session before retrying
							try {
								const { data: { session: freshSession } } = await supabase.auth.getSession();
								if (freshSession && freshSession.access_token) {
									console.log("Got fresh session, retrying profile fetch...");
									// Use fresh session for next retry
									currentSession = freshSession;
								}
							} catch (e) {
								console.warn("Error getting fresh session:", e);
							}
							
							retryCount++;
							continue;
						}

						// For other errors or max retries reached, break
						break;
					}

					// Handle profile fetch results
					if (profileError) {
						if (profileError.code === "PGRST116") {
							// Profile doesn't exist - log but don't throw
							console.warn("Profile not found for user:", currentSession.user.id, "- User can still login but profile needs to be created");
							// Set user with null profile instead of logging out
							// This allows the user to stay logged in and complete their profile
							setUser({
								...currentSession.user,
								profile: null,
							});
						} else if (profileError.status === 406 || profileError.code === "PGRST301") {
							// 406 Not Acceptable after retries - log full error details
							console.error("Profile fetch failed with 406 after retries:", {
								code: profileError.code,
								message: profileError.message,
								details: profileError.details,
								status: profileError.status,
								userId: currentSession.user.id,
								hint: "Profile may exist but token is invalid. Check browser console for token errors."
							});
							// Still set user with null profile to allow login
							// The profile might exist but token is invalid
							setUser({
								...currentSession.user,
								profile: null,
							});
						} else {
							// Other profile errors - log full details
							console.error("Error fetching profile:", {
								code: profileError.code,
								message: profileError.message,
								details: profileError.details,
								status: profileError.status,
								userId: currentSession.user.id
							});
							setUser({
								...currentSession.user,
								profile: null,
							});
						}
					} else if (profile) {
						// Successfully fetched profile
						console.log("Successfully fetched profile for user:", currentSession.user.id);
						setUser({
							...currentSession.user,
							profile,
						});
						// Reset refresh attempts on successful auth
						refreshAttempts = 0;
					} else {
						// No profile and no error - shouldn't happen but handle it
						console.warn("No profile data returned for user:", currentSession.user.id);
						setUser({
							...currentSession.user,
							profile: null,
						});
					}
				} else {
					setUser(null);
					refreshAttempts = 0;
				}
			} catch (error) {
				console.error("Error handling auth change:", error);
				
				// For errors, keep user logged in if session exists
				// Only logout if there's no session
				if (!session?.user) {
					setUser(null);
				} else {
					// Keep user logged in even on errors
					setUser({
						...session.user,
						profile: null,
					});
				}
			} finally {
				isHandlingAuth = false;
				setLoading(false);
			}
		};

		// Check initial session
		supabase.auth.getSession().then(({ data: { session } }) => {
			handleAuthChange(session);
		});

		// Listen for auth changes
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			handleAuthChange(session);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	// CRITICAL: Memoize the context value to prevent unnecessary re-renders
	const contextValue = useMemo(
		() => ({
			user,
			loading,
			supabase,
		}),
		[user, loading]
	); // Don't include supabase as it's stable

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

export default AuthContext;
